import {AttributeMetadataInterface} from "~/interfaces/attributeMetadataInterface";
import IDefaultSortOptions from "~/interfaces/defaultSortOptions";
import {IBuiltinObjectModelProperty} from "~/interfaces/objectModelInterfaces";
import IConditionalRuleReferencedControl from "~/models/conditionalRules/referencedControl";
import ObjectModelRelation from "~/models/objectModelRelations/objectModelRelation";
import Folder from "../../../formability/document_management/models/folder";
import Selected from "../../../formability/listViewBuilder/store/modules/selected";
import {DocumentType} from "../../enums/documentType";
import {ObjectHelper} from "../../helpers/objectHelper";
import IRootBuilderModel from "../../interfaces/builderModelInterfaces";
import {Hash} from "../../interfaces/hashInterfaces";
import {IListView} from "../../interfaces/listViewInterfaces";
import {IOrganizationDocument} from "../../interfaces/organizationDocumentInterfaces";
import IRepo from "../../interfaces/repo";
import Validatable from "../../mixins/validatable";
import ListViewProvider from "../../providers/listViewProvider";
import ElementHandler from "../../services/elementHandler";
import {IPeekerOptions} from "../../services/peeker";
import {SortOption} from "../../sortOption";
import Repo from "../../state/modules/repo";
import store from "../../state/store";
import {ConditionalFieldDefinition} from "../conditionalRules/conditionalFieldDefinition";
import IConditionalRuleContainer from "../conditionalRules/conditionalRuleContainer";
import ConditionalRuleSetFactory from "../conditionalRules/conditionalRuleSetFactory";
import ContentElementable from "../contentManagement/contentElementable";
import FilterElementable from "../contentManagement/filterElementable";
import PublishedListViewDecorator from "../decorators/publishedListViewDecorator";
import ListViewBuilderValidation from "../navigableValidations/listViewBuilderValidation";
import ObjectModel from "../objectModel";
import ObjectModelAction from "../objectModelActions/objectModelAction";
import ObjectModelProperty from "../objectModelProperties/objectModelProperty";
import ObjectModelConditionalFields from "../objectModels/objectModelConditionalFields";
import ObjectModelConditionalRuleSet from "../objectModels/objectModelConditionalRuleSet";
import PublishedObjectModel from "../objectModels/publishedObjectModel";
import Organization from "../organization";
import {RelationType} from "../relationType";
import ListViewBuilderModel from "../tracked/listViewBuilderModel";
import ListViewContentElement from "./elements/listViewContentElement";
import ListViewFilterElement from "./elements/listViewFilterElement";
import ListViewMainAction from "./elements/listViewMainAction";
import ListViewImageUpload from "./listViewImageUpload";
import {ListViewPredefinedFilterMode} from "./listViewPredefinedFilterMode";
import * as _ from "lodash"
import Share from "~/models/share";
import Shareable from "~/mixins/shareable";
import Capable from "~/mixins/capable";
import MainActionable from "~/models/contentManagement/mainActionable";

export default class ListView extends MainActionable(FilterElementable(ContentElementable(Validatable(Shareable(Capable(ListViewBuilderModel))))))
  implements IRootBuilderModel, IOrganizationDocument, IListView, IConditionalRuleContainer {
  _classKey = "listView";

  get collectionKey() {
    return "list_views";
  }
  listView = this;
  _organization: Organization;
  folder: any;
  attributes_metadata: { [key: string]: AttributeMetadataInterface };
  author: any;
  folder_id: string;
  title: string;
  short_description: string;
  show_description: boolean;
  u_at: string;
  c_at: string;
  attempted_to_publish: boolean;
  _contentElementHandler = new ElementHandler(
    this,
    ListView,
    "content_elements"
  );
  _filterElementHandler = new ElementHandler(this, ListView, "filter_elements");
  _mainActionHandler = new ElementHandler(this, ListView, "main_actions");
  object_model: ObjectModel;
  object_model_id: string;
  published_object_model?: PublishedObjectModel;
  image_uploads: ListViewImageUpload[];
  pin_count: number;
  pin_count_max: number;
  predefined_filter_mode: ListViewPredefinedFilterMode;
  predefined_filter_conditional_rule_sets: Hash<
    ObjectModelConditionalRuleSet
  >;
  viewers_can_take_actions: boolean;
  viewers_can_take_bulk_actions: boolean;
  viewers_can_take_main_actions: boolean;
  bulk_action_client_ids: string[];
  hide_data_without_search: boolean;
  only_show_results_search_bar_client_id: string;
  _publishedRecord: PublishedListViewDecorator;
  allow_create_instances: boolean;
  default_sort_options: IDefaultSortOptions[];
  filterQuery = {};
  model_property_content_elements_json: Array<IBuiltinObjectModelProperty>;
  show_seo: boolean
  seo_title: string;
  seo_slug: string;
  seo_keywords: string[];
  seo_meta_description: string;

  except = [
    "listView",
    "createEmbeddedObject",
    "organization",
    "saveTimer",
    "_organization",
    "shares",
  ];

  static repo: IRepo<ListView, ListViewProvider> = new Repo<
    ListView,
    ListViewProvider
  >(
    "listViews",
    ListView,
    {},
    new ListViewProvider(ListView, "list_view", "list_views")
  );
  repo: IRepo<this, ListViewProvider<this>>;

  static get provider(): ListViewProvider {
    return this.repo.provider;
  }

  get provider(): this["repo"]["provider"] {
    return this.repo.provider;
  }

  static get displayName(): string {
    return "List View";
  }

  get predefinedFilterRuleset() : ObjectModelConditionalRuleSet{
    const ruleSets = this.predefined_filter_conditional_rule_sets;
    const key = Object.keys(ruleSets)[0];
    if (key) {
      return ruleSets[key];
    } else {
      return null;
    }
  }

  constructor() {
    super();
    this.defineProperty(
      "predefined_filter_mode",
      ListViewPredefinedFilterMode.show
    );
    this.defineProperty("bulk_action_client_ids", []);
    this.defineProperty("hide_data_without_search", false);
    this.defineProperty("only_show_results_search_bar_client_id", null);

    for (const definition of ObjectModelConditionalFields.LIST_VIEW_FIELDS) {
      const conditionalFieldName = `${definition.fieldName}_conditional_rule_sets`;
      this.except.push(conditionalFieldName);
      this[conditionalFieldName] = {};
      this.createEmbeddedObject[conditionalFieldName] = (parent, json) => {
        return ConditionalRuleSetFactory.createForContainer(
          this,
          definition,
          {},
          true,
          false
        );
      };
      if (definition.defaultConditionalValue instanceof Function) {
        this.except.push(definition.fieldName);
        this.createEmbeddedObject[definition.fieldName] =
          definition.defaultConditionalValue;
      }
    }
  }

  get TRACKED_ARRAYS(): string[] {
    return ["default_sort_options"];
  }

  get UNTRACKED_PROPERTIES(): string[] {
    return ["u_at", "image_uploads", "folder"];
  }

  // stored statically so that PublishedListView can inherit from ListView's embedded models because you can't use
  // super to get properties when targeting <ES6
  static EMBEDDED_MODELS: { [key: string]: { type: any; relationType: RelationType } } = (() => {
    const models = {
      content_elements: {
        type: ListViewContentElement,
        relationType: RelationType.dictionary
      },
      filter_elements: {
        type: ListViewFilterElement,
        relationType: RelationType.dictionary
      },
      object_model: {
        type: ObjectModel,
        relationType: RelationType.singleObject
      },
      shares: {
        type: Share,
        relationType: RelationType.collection
      },
      main_actions: {
        type: ListViewMainAction,
        relationType: RelationType.dictionary
      },
    };
    for (const definition of ObjectModelConditionalFields.LIST_VIEW_FIELDS) {
      models[`${definition.fieldName}_conditional_rule_sets`] = {
        type: ObjectModelConditionalRuleSet,
        relationType: RelationType.dictionary
      };
      if (definition.defaultConditionalValue instanceof Function) {
        models[definition.fieldName] = {
          type: Object,
          relationType: RelationType.singleObject
        };
      }
    }
    return models;
  })();

  get EMBEDDED_MODELS(): {
    [key: string]: { type: any; relationType: RelationType };
  } {
    return ListView.EMBEDDED_MODELS;
  }

  createEmbeddedObject = {
    content_elements: (parent, json): ListViewContentElement =>
      new ListViewContentElement(),
    filter_elements: (parent, json): ListViewFilterElement =>
      new ListViewFilterElement(),
    main_actions: (parent, json): ListViewMainAction =>
      new ListViewMainAction(),
    object_model: (parent, json): ObjectModel => new ObjectModel()
  };

  get rootName() {
    return "listView";
  }

  get classKey(): string {
    return "listView";
  }

  get storeName(): string {
    return `${this.rootName}Store`;
  }

  get organization(): Organization {
    if (this._organization) {
      // for TrackedModel implementation
      return this._organization;
    } else if (this.parent instanceof Organization) {
      // for OrganizationDocument implementation
      return this.parent;
    } else {
      return this.parent.organization;
    }
  }

  set organization(val: Organization) {
    this._organization = val;
  }

  // ******************** VALIDATABLE******************** //
  _validation: ListViewBuilderValidation = new ListViewBuilderValidation(this);

  getDisplayName(propName: string, value?): string {
    switch (propName) {
      case 'default_sort_options': // intentional fallthrough
      case 'short_description':
      case 'seo_title':
      case 'seo_slug':
      case 'seo_keywords':
      case 'seo_meta_description':
        return this.attributes_metadata[propName].display_name;
      default:
        return propName;
    }
  }

  // ******************** VALIDATABLE END******************** //

  // ******************** TRACK CHANGES******************** //

  get hasChanged() {
    return this._hasChanged;
  }

  set hasChanged(newVal: boolean) {
    if (newVal) {
      this.queueSendChangesToServer(
        this.lastPeekerOptions || {
          area: this.currentBuilderArea(),
          modelId: this.clientIdOrId
        }
      );
      if (this.attempted_to_publish) {
        this.validate();
      }
    }
    this._hasChanged = newVal;
  }

  saveToServer(): Promise<any> {
    return store.dispatch(`${this.storeName}/saveToServer`).then(res => {
      this.handleErrorsAndUpdateSaveMessage(res.better_errors)
    }).catch(err => {
      this.registerAutosaveError(err);
      let message = `Error: ${err.status} - ${err.statusText}`;
      if (window.environment === "test") {
        message += ` - ${err.responseText}`
      }
      store.commit('flashStore/set', {
        flash: {
          alert: message
        }
      });
    });
  }

  protected saveChanges(
    changes: any,
    peekerOptions: string | IPeekerOptions = {
      area: this.currentBuilderArea(),
      modelId: this.clientIdOrId
    }
  ): void {
    store.commit(`${this.storeName}/saveChanges`, { changes, peekerOptions });
  }

  // ******************** TRACK CHANGES END ******************** //

  get isPublished(): boolean {
    return this.is_published && Date.now() > Date.parse(this.u_at);
  }

  get titleOrDefault(): string {
    if (this.title && this.title.length > 0) {
      return this.title;
    } else {
      return "Untitled List View";
    }
  }

  get isArchive(): boolean {
    return (
      this.parent instanceof Folder &&
      (this.parent.name === "Archive" || this.parent.name === "Trash")
    );
  }

  canMoveTo(folder: Folder): boolean {
    return !folder.isArchiveFolder();
  }

  //------------------------ Selected
  get selectedContentElement(): ListViewContentElement {
    return Selected.listViewContentElement.get();
  }

  get selectedFilterElement(): ListViewFilterElement {
    return Selected.listViewFilterElement.get();
  }

  get selectedMainAction(): ListViewMainAction {
    return Selected.listViewMainAction.get();
  }

  getConditionalRuleSets(
    fieldName: string
  ): Hash<ObjectModelConditionalRuleSet> {
    return this[`${fieldName}_conditional_rule_sets`];
  }

  getSortedConditionalRuleSets(
    fieldName: string
  ): ObjectModelConditionalRuleSet[] {
    const ruleSets = this.getConditionalRuleSets(fieldName);
    return ObjectHelper.toArray(ruleSets).sort((x, y) =>
      x.sort_order < y.sort_order ? -1 : 1
    );
  }

  createNewRuleSet(
    fieldName: string,
    definition: ConditionalFieldDefinition
  ): ObjectModelConditionalRuleSet {
    return new ObjectModelConditionalRuleSet(fieldName, definition);
  }

  getReferencedControl(id: string): ObjectModelProperty | ObjectModelRelation {
    return ObjectModelProperty.repo.find(id) || ObjectModelRelation.repo.find(id);
  }

  getReferencableControls() {
    return [...this.getReferencableObjectModelProperties(), ...this.getReferencableAccountRelationships()]
  }

  get objectModel() {
    return this.object_model
  }

  getReferencableObjectModelProperties(): ObjectModelProperty[] {
    return this.object_model.objectModelPropertiesArray.filter(property => property.objectModel.isPropertyIndexed(property))
  }

  getReferencableAccountRelationships() : ObjectModelRelation[]{
    return this.object_model.accountRelationsArray
  }

  getGroupedReferencableControls(): { [key: string]: IConditionalRuleReferencedControl[] } {
    return {
      "Account": this.getReferencableAccountRelationships(),
      "Indexed Properties": this.getReferencableObjectModelProperties()
    }
  }


  //------------------------ Default Sorting
  addDefaultSortOption(propertyId: string): void {
    const property = this.objectModelProperty(propertyId);
    this.default_sort_options.push({ id: property.client_id, name: property.label_title, orderBy: SortOption.ASC });
  }

  removeDefaultSortOption(propertyId: string): void {
    _.remove(this.default_sort_options, option => option.id === propertyId);
  }

  moveUpDefaultSortOption(propertyId: string): void {
    const selectedOptionIndex = this.default_sort_options.findIndex(option => option.id === propertyId);

    if (selectedOptionIndex > 0) {
      const otherOption = { ...this.default_sort_options[selectedOptionIndex - 1] };
      this.default_sort_options[selectedOptionIndex - 1] = this.default_sort_options[selectedOptionIndex];
      this.default_sort_options[selectedOptionIndex] = otherOption;
    }
  }

  moveDownDefaultSortOption(propertyId: string): void {
    const selectedOptionIndex = this.default_sort_options.findIndex(option => option.id === propertyId);

    if (selectedOptionIndex < this.default_sort_options.length - 1) {
      const otherOption = { ...this.default_sort_options[selectedOptionIndex + 1] };
      this.default_sort_options[selectedOptionIndex + 1] = this.default_sort_options[selectedOptionIndex];
      this.default_sort_options[selectedOptionIndex] = otherOption;
    }
  }

  setDefaultSortMethod(propertyId: string, value: SortOption): void {
    const selectedOptionIndex = this.default_sort_options.findIndex(option => option.id === propertyId);

    const clone = [...this.default_sort_options];
    clone[selectedOptionIndex].orderBy = value;

    // this.default_sort_options = clone;
    this.default_sort_options.length = 0;
    clone.forEach((value2: any) => {
      this.default_sort_options.push(value2);
    });
  }

  objectModelProperty(id: string): ObjectModelProperty {
    return ObjectModelProperty.repo.find(id);
  }

  indexedObjectModelProperties(): ObjectModelProperty[] {
    return this.object_model.objectModelPropertiesArray.filter(prop => prop.isIndexed);
  }

  //------------------------ Content Elements
  get firstRow(): ListViewContentElement {
    return this.contentElementHandler.all[0];
  }

  get pinnedColumns(): ListViewContentElement[] {
    return this.firstRow.contentElementHandler.all.filter(ce => ce.isPinned);
  }

  get unpinnedColumns(): ListViewContentElement[] {
    return this.firstRow.contentElementHandler.all.filter(ce => !ce.isPinned);
  }

  get docType(): DocumentType {
    return DocumentType[this.classKey];
  }

  get bulkActions(): ObjectModelAction[] {
    let objectModel = this.published_object_model || this.object_model
    return objectModel.allObjectModelActions.filter(a =>
      this.bulk_action_client_ids.includes(a.client_id)
    );
  }

  async fetchPublishedRecord(): Promise<any> {
    // TODO: Use this similar to object models
  }

  buildPublishedRecord(json: object = {}): this["_publishedRecord"] {
    this._publishedRecord = new PublishedListViewDecorator(json);
    return this.publishedRecord;
  }

  get publishedRecord(): this["_publishedRecord"] {
    return this._publishedRecord;
  }

  _moving: boolean = false
  get moving(): boolean {
    return this._moving
  }

  set moving(val: boolean) {
    this._moving = val
  }
}
